import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ProductCard from './components/ProductCard';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import CheckoutModal from './components/CheckoutModal';
import TrackOrderModal from './components/TrackOrderModal';
import { ShopProvider, useShop } from './context/ShopContext';
import ScienceSection from './components/ScienceSection';
import { TermsPage, PrivacyPage, ShippingPage, ReturnsPage, DisclaimerPage } from './pages/LegalPages';

// Indicative launch pricing, benchmarked against the Indian D2C market (Jul 2026):
// dried fruiting body retails ~₹50/g; C. militaris 500mg×60 capsules ~₹700–1,000.
// Value formats (tea / chai / rice cakes) priced as approachable everyday SKUs.
const products = [
  {
    id: 1,
    title: "Whole Dried Fruiting Bodies (10g)",
    priceInr: 499,
    priceUsd: 6.49,
    tag: "WHOLE FRUITING BODY",
    image: "/media/whole.jpg",
    description: "Whole Cordyceps militaris fruiting bodies, cultivated on our own substrate and low-heat / sun-dried to preserve their deep golden colour and aroma. Nothing added.",
    features: ["100% Fruiting Body", "Low-Heat / Sun-Dried", "Lab-Tested Safety"]
  },
  {
    id: 2,
    title: "500mg HPMC Capsules (60 count)",
    priceInr: 899,
    priceUsd: 10.99,
    tag: "EASY DAILY FORMAT",
    image: "/media/capsules.jpg",
    description: "Milled whole fruiting body in 500 mg vegetarian HPMC capsules — a simple, taste-free daily format. 60 capsules per bottle. No fillers, no flow agents.",
    features: ["500 mg × 60", "Vegan HPMC Shell", "No Fillers"]
  },
  {
    id: 3,
    title: "Cordyceps Dip Tea (15 sachets)",
    priceInr: 349,
    priceUsd: 4.49,
    tag: "CAFFEINE-FREE",
    image: "/media/tea.jpg",
    description: "Cut Cordyceps militaris in ready-to-brew infusion bags — just cordyceps, with no tea leaf and no caffeine. Earthy, savoury and warming.",
    features: ["No Tea Leaf", "Caffeine-Free", "15 Sachets"]
  },
  {
    id: 4,
    title: "Cordyceps Masala Chai (100g)",
    priceInr: 399,
    priceUsd: 4.99,
    tag: "SPICED BLEND",
    image: "/media/chai.jpg",
    description: "A spiced masala blend of Cordyceps militaris with cardamom, ginger, cinnamon and clove. Brewed like chai but with no tea leaf and no caffeine — add milk or water.",
    features: ["No Tea Leaf", "Real Whole Spices", "Caffeine-Free"]
  },
  {
    id: 5,
    title: "Cordyceps Cultured Rice Cakes",
    priceInr: 399,
    priceUsd: 4.99,
    tag: "WHOLE CULTURE",
    image: "/media/ricecake.jpg",
    description: "The whole colonised rice cake — Cordyceps militaris grown on its rice substrate and dried food-grade. Only clean, fully-fruited cakes are sold. Snap into brews or broths.",
    features: ["Whole Culture Block", "Food-Grade Dried", "Small-Batch"]
  }
];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Home = ({ setIsTrackOpen }) => {
  const { currency, addToCart } = useShop();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [hash]);
  
  return (
    <>
      <Navbar onOpenTrack={() => setIsTrackOpen(true)} />
      <Hero />

      <main>
        <section id="products" style={{ padding: '8rem 5%', background: '#000000', position: 'relative' }}>
          <div className="container">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              textAlign: 'center',
              border: '1px solid #2a2a2a',
              background: '#0a0a0a',
              borderRadius: '8px',
              padding: '12px 20px',
              marginBottom: '3.5rem',
              color: '#aaaaaa',
              fontSize: '0.82rem',
              lineHeight: 1.5
            }}>
              <span style={{ color: '#ffffff', fontWeight: 700, letterSpacing: '0.04em' }}>PRE-LAUNCH</span>
              <span>Every order is a pre-order — dispatch begins once our FSSAI Central licence is issued. Prices are indicative.</span>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
              <div style={{
                color: '#888888',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '12px'
              }}>
                Our Collection
              </div>
              <h2 style={{
                fontSize: 'clamp(2.6rem, 5.5vw, 4.2rem)',
                lineHeight: 1.05,
                color: '#ffffff',
                fontWeight: 900,
                letterSpacing: '-0.04em'
              }}>
                THE CORDYCEPS RANGE.
              </h2>
              <p style={{ color: '#888888', maxWidth: '640px', margin: '16px auto 0', fontSize: '1.05rem', lineHeight: 1.65 }}>
                Five honest formats of one mushroom — <em>Cordyceps militaris</em> — from whole dried fruiting bodies to capsules, tea, masala chai and our cultured rice cakes. Every batch is third-party lab-tested for heavy metals and microbial safety.
              </p>
            </div>

            <div className="product-grid" style={{
              display: 'grid',
              gap: '32px',
            }}>
              {products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  {...product}
                  price={`${currency.symbol}${currency.code === 'INR' ? product.priceInr : product.priceUsd}`}
                  delay={index}
                  onAddToCart={() => addToCart(product)}
                />
              ))}
            </div>
          </div>
        </section>

        <section id="science">
          <ScienceSection />
        </section>
      </main>

      <Footer />
      <style>{`
        .product-grid {
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
        }
        @media (max-width: 768px) {
          .product-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

const App = () => {
  const [isTrackOpen, setIsTrackOpen] = useState(false);

  return (
    <ShopProvider>
      <Router>
        <ScrollToTop />
        <div className="App" style={{ background: '#000000', minHeight: '100vh', color: '#ffffff' }}>
          <CartDrawer />
          <CheckoutModal />
          <TrackOrderModal isOpen={isTrackOpen} onClose={() => setIsTrackOpen(false)} />

          <Routes>
            <Route path="/" element={<Home setIsTrackOpen={setIsTrackOpen} />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/shipping" element={<ShippingPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
          </Routes>
        </div>
      </Router>
    </ShopProvider>
  );
};

export default App;
